import ErrorComponent from '@mui/icons-material/Error';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { Link } from '@openx/components-core/lib/Link/Link.tsx';
import type { ListLalSegment } from '@openx/types/segments.ts';
import { lalSegmentStatus, segmentSoonWillBeExpired } from '@openx/types/segments.ts';
import { isSoonToDate } from '@openx/utils/lib/date.ts';
import { useTranslation } from 'react-i18next';

type Props = {
  lalSegment: Pick<ListLalSegment, 'id' | 'lookalike_name' | 'status' | 'auc_score' | 'derived_expiry_date' | 'error'>;
};

const tooltipTitleStyle = { fontSize: 12, lineHeight: 1.5 };

const expiredText = 'Segment data is expired and cannot be used for LAL Segment creation.';
const soonWillBeExpiredText = segmentSoonWillBeExpired(false);

export const NameCell = ({ lalSegment }: Props) => {
  const { t } = useTranslation();

  const { lookalike_name: name, status, derived_expiry_date: expiryDate, error } = lalSegment;
  const hasError = status === lalSegmentStatus.ERROR && error?.message;

  const showExpiredAlert = status === lalSegmentStatus.EXPIRED;
  const showExpireSoonAlert = !showExpiredAlert && isSoonToDate(expiryDate);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: 274,
        paddingTop: 0.5,
        paddingBottom: 0.5,
      }}
    >
      <Typography
        variant="body1Bold"
        data-test="row-title"
        sx={{
          mr: 0.625,
          lineHeight: '22px',
          color: 'primary.main',
        }}
      >
        <Link to={`/segments/lookalike/${lalSegment.id}`}>{name}</Link>

        {hasError && (
          <AlertTooltip
            color={theme => theme.palette.error.main}
            title={<Typography sx={tooltipTitleStyle}>{t(error.message)}</Typography>}
            icon={<ErrorComponent fontSize="small" color="inherit" />}
            sx={{ mt: -0.5, padding: 0 }}
          />
        )}

        {showExpiredAlert && (
          <AlertTooltip
            color={theme => theme.palette.error.main}
            title={<Typography sx={tooltipTitleStyle}>{t(expiredText)}</Typography>}
            icon={<ErrorComponent fontSize="small" color="inherit" />}
            sx={{ mt: -0.5, padding: 0 }}
          />
        )}

        {showExpireSoonAlert && (
          <AlertTooltip
            color={theme => theme.palette.warning.main}
            title={<Typography sx={tooltipTitleStyle}>{t(soonWillBeExpiredText)}</Typography>}
            icon={<Warning fontSize="small" color="inherit" />}
            sx={{ mt: -0.5, padding: 0 }}
          />
        )}
      </Typography>
    </Box>
  );
};
